import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const GROUPS = [
  {
    key: "association",
    links: [
      ["/association", "navX.presentation"],
      ["/mission", "nav.mission"],
      ["/histoire", "nav.histoire"],
      ["/evenements", "navX.evenements"],
      ["/transparence", "nav.transparence"],
    ],
  },
  {
    key: "research",
    links: [
      ["/recherche", "nav.recherche"],
      ["/centre", "nav.centre"],
      ["/programme", "nav.programme"],
      ["/projets", "navX.projets"],
      ["/feuille-de-route", "nav.roadmap"],
    ],
  },
  {
    key: "join",
    links: [
      ["/scientifiques", "nav.scientifiques"],
      ["/partenariats", "nav.partenariats"],
      ["/soutenir", "nav.soutenir"],
    ],
  },
];

const DIRECT = [
  ["/actualites", "nav.actualites"],
  ["/ressources", "nav.ressources"],
  ["/contact", "nav.contact"],
];

const ALL_LINKS = [
  ["/association", "navX.presentation"],
  ["/mission", "nav.mission"],
  ["/histoire", "nav.histoire"],
  ["/evenements", "navX.evenements"],
  ["/transparence", "nav.transparence"],
  ["/recherche", "nav.recherche"],
  ["/centre", "nav.centre"],
  ["/programme", "nav.programme"],
  ["/projets", "navX.projets"],
  ["/feuille-de-route", "nav.roadmap"],
  ["/scientifiques", "nav.scientifiques"],
  ["/partenariats", "nav.partenariats"],
  ["/soutenir", "nav.soutenir"],
  ["/actualites", "nav.actualites"],
  ["/ressources", "nav.ressources"],
  ["/contact", "nav.contact"],
];

const Logo = ({ light = false }) => (
  <Link to="/" className="flex items-center gap-3" data-testid="nav-logo" aria-label="Green Horizon Project">
    <img src="/assets/logo.png" alt="" className="h-11 w-11 bg-white object-contain p-0.5" />
    <span className="hidden sm:block">
      <span className={`block font-display text-sm font-extrabold uppercase tracking-tight leading-none ${light ? "text-white" : "text-graphite"}`}>
        Green Horizon
      </span>
      <span className={`block font-display text-sm font-light uppercase tracking-[0.35em] leading-none mt-0.5 ${light ? "text-white/70" : "text-graphite-soft"}`}>
        Project
      </span>
    </span>
  </Link>
);

const LangToggle = ({ light = false }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div
      className={`flex border ${light ? "border-white/30" : "border-graphite/20"}`}
      role="group"
      aria-label="Language / Langue"
    >
      {["fr", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-toggle-${l}`}
          className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-200 ${
            lang === l
              ? "bg-forest text-white"
              : light
              ? "text-white/70 hover:text-white"
              : "text-graphite-soft hover:text-graphite"
          }`}
          aria-pressed={lang === l}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const Navbar = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const linkCls = ({ isActive }) =>
    `font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-200 ${
      isActive ? "text-forest" : "text-graphite-soft hover:text-graphite"
    }`;

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-graphite/10 bg-bone/85 backdrop-blur-xl" data-testid="main-navbar">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <Logo />
          <nav className="hidden items-center gap-7 lg:flex" aria-label="Navigation principale">
            {GROUPS.map((g) => (
              <div key={g.key} className="group relative">
                <button
                  className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-graphite-soft transition-colors duration-200 hover:text-graphite"
                  data-testid={`nav-group-${g.key}`}
                  aria-haspopup="true"
                >
                  {t(`nav.${g.key}`)}
                  <ChevronDown className="h-3 w-3 transition-transform duration-200 group-hover:rotate-180" />
                </button>
                <div className="invisible absolute left-0 top-full pt-3 opacity-0 transition-opacity duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                  <div className="min-w-[240px] border border-graphite/10 bg-white py-2 shadow-sm">
                    {g.links.map(([to, key]) => (
                      <NavLink
                        key={to}
                        to={to}
                        className="block px-5 py-2.5 text-sm text-graphite-soft transition-colors duration-150 hover:bg-forest-soft hover:text-forest"
                        data-testid={`nav-link-${key.split(".")[1]}`}
                      >
                        {t(key)}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            {DIRECT.map(([to, key]) => (
              <NavLink key={to} to={to} className={linkCls} data-testid={`nav-link-${key.split(".")[1]}`}>
                {t(key)}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <LangToggle />
            <Link
              to="/scientifiques"
              className="group hidden items-center gap-2 bg-forest px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-graphite md:inline-flex"
              data-testid="nav-cta-participer"
            >
              {t("nav.participate")}
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <button
              className="text-graphite lg:hidden"
              onClick={() => setOpen(true)}
              aria-label={t("nav.menu")}
              data-testid="mobile-menu-button"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] flex flex-col bg-graphite"
            data-testid="mobile-menu"
          >
            <div className="flex h-16 items-center justify-between border-b border-white/10 px-6">
              <Logo light />
              <button onClick={() => setOpen(false)} aria-label={t("nav.close")} className="text-white" data-testid="mobile-menu-close">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-6 py-10" aria-label="Menu mobile">
              <ul className="space-y-1">
                {ALL_LINKS.map(([to, key], i) => (
                  <motion.li
                    key={to}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 + i * 0.03, duration: 0.35 }}
                  >
                    <Link
                      to={to}
                      onClick={() => setOpen(false)}
                      className={`block border-b border-white/10 py-3.5 font-display text-2xl font-light tracking-tight transition-colors duration-200 ${
                        location.pathname === to ? "text-[#4ADE80]" : "text-white hover:text-[#4ADE80]"
                      }`}
                      data-testid={`mobile-nav-link-${key.split(".")[1]}`}
                    >
                      {t(key)}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </nav>
            <div className="border-t border-white/10 px-6 py-5">
              <LangToggle light />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
